import React, { useState, useEffect, useRef } from 'react';
import { 
  TextField, 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  InputBase,
  Stack,
  Input,
  Menu,
  Chip,
} from '@mui/material';
import { 
  Help as HelpIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
  Functions as FunctionsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  Percent as PercentIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const INITIAL_COLUMNS = [
  { 
    id: 'A', 
    label: 'A', 
    type: 'text', 
    isFormula: false,
    backgroundColor: '#ffffff',
    textAlign: 'left',
    width: 100
  },
  { 
    id: 'B', 
    label: 'B', 
    type: 'text', 
    isFormula: false,
    backgroundColor: '#ffffff',
    textAlign: 'left',
    width: 100
  },
  { 
    id: 'C', 
    label: 'C', 
    type: 'text', 
    isFormula: false,
    backgroundColor: '#ffffff',
    textAlign: 'left',
    width: 100
  },
  { 
    id: 'D', 
    label: 'D', 
    type: 'text', 
    isFormula: false,
    backgroundColor: '#ffffff',
    textAlign: 'left',
    width: 100
  }
];

const OPERATORS = [
  { value: '+', label: 'Topla' },
  { value: '-', label: 'Çıkar' },
  { value: '*', label: 'Çarp' },
  { value: '/', label: 'Böl' },
  { value: '&', label: 'Birleştir' },
  { value: 'TOPLA', label: 'Topla' },
  { value: 'ORTALAMA', label: 'Ortalama' },
  { value: 'EĞER', label: 'Eğer' },
  { value: 'MAK', label: 'Maksimum' },
  { value: 'MİN', label: 'Minimum' },
  { value: 'SAY', label: 'Say' },
  { value: 'DÜŞEYARA', label: 'Düşeyara' },
  { value: 'EĞERSAY', label: 'Eğersay' },
  { value: 'EĞERTOPLA', label: 'Eğertopla' },
  { value: 'BİRLEŞTİR', label: 'Birleştir' },
  { value: 'SOL', label: 'Soldan' },
  { value: 'SAĞ', label: 'Sağdan' },
  { value: 'UZUNLUK', label: 'Uzunluk' },
  { value: 'TEMİZLE', label: 'Temizle' },
  { value: 'BÜYÜKHARF', label: 'Büyük Harf' },
  { value: 'KÜÇÜKHARF', label: 'Küçük Harf' }
];

const COLUMN_TYPES = [
  { value: 'text', label: 'Metin' },
  { value: 'number', label: 'Sayı' },
  { value: 'date', label: 'Tarih' },
  { value: 'formula', label: 'Formül' },
  { value: 'currency', label: 'Para Birimi' }
];

const COLOR_PALETTE = [
  { name: 'Beyaz', value: '#ffffff' },
  { name: 'Açık Gri', value: '#f5f5f5' },
  { name: 'Açık Mavi', value: '#e3f2fd' },
  { name: 'Açık Yeşil', value: '#e8f5e9' },
  { name: 'Açık Sarı', value: '#fff3e0' },
  { name: 'Açık Kırmızı', value: '#ffebee' },
  { name: 'Mavi', value: '#bbdefb' },
  { name: 'Yeşil', value: '#c8e6c9' },
  { name: 'Sarı', value: '#ffe0b2' },
  { name: 'Kırmızı', value: '#ffcdd2' },
  { name: 'Mor', value: '#e1bee7' },
  { name: 'Turkuaz', value: '#b2ebf2' }
];

const CalculationField = ({ field, value, onChange, formData }) => {
  let parsedValue = value;
  if (typeof value === 'string') {
    try {
      parsedValue = JSON.parse(value);
    } catch {
      parsedValue = { columns: [], rows: [] };
    }
  }

  const [columns, setColumns] = useState(parsedValue?.columns || [
    { id: 'A', label: 'A', width: 100, backgroundColor: '#ffffff', type: 'number' },
    { id: 'B', label: 'B', width: 100, backgroundColor: '#ffffff', type: 'number' },
    { id: 'C', label: 'C', width: 100, backgroundColor: '#ffffff', type: 'number' },
    { id: 'D', label: 'D', width: 100, backgroundColor: '#ffffff', type: 'number' }
  ]);
  
  const [rows, setRows] = useState(parsedValue?.rows || [
    { id: 1, A: '', B: '', C: '', D: '', colors: {} },
    { id: 2, A: '', B: '', C: '', D: '', colors: {} },
    { id: 3, A: '', B: '', C: '', D: '', colors: {} },
    { id: 4, A: '', B: '', C: '', D: '', colors: {} }
  ]);
  
  const [selectedCells, setSelectedCells] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [formulaDialog, setFormulaDialog] = useState(false);
  const [colorDialog, setColorDialog] = useState(false);
  const [addColumnDialog, setAddColumnDialog] = useState(false);
  const [formula, setFormula] = useState('');
  const [newColumn, setNewColumn] = useState({ label: '', width: 100, backgroundColor: '#ffffff' });
  const tableRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [columnDialog, setColumnDialog] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [columnSettings, setColumnSettings] = useState({
    label: '',
    width: 100,
    backgroundColor: '#ffffff',
    type: 'number',
    isFormula: false,
    formula: ''
  });

  const [selectedCell, setSelectedCell] = useState(null);
  const [showFormulas, setShowFormulas] = useState(false);

  const handleCellChange = (rowId, columnId, value) => {
    const rowIndex = rows.findIndex(r => r.id === rowId);
    if (rowIndex !== -1) {
      const newRows = [...rows];
      newRows[rowIndex] = {
        ...newRows[rowIndex],
        [columnId]: value
      };
      setRows(newRows);
    }
  };

  const handleCellClick = (rowId, columnId, event) => {
    if (event.ctrlKey || event.metaKey) {
      const exists = selectedCells.some(cell => cell.rowId === rowId && cell.columnId === columnId);
      if (exists) {
        setSelectedCells(prev => prev.filter(cell => !(cell.rowId === rowId && cell.columnId === columnId)));
      } else {
        setSelectedCells(prev => [...prev, { rowId, columnId }]);
      }
    } else {
      setSelectedCells([{ rowId, columnId }]);
    }
  };

  const handleCellDoubleClick = (rowId, columnId) => {
    setEditingCell({ rowId, columnId });
  };

  const handleCellRightClick = (event, rowId, columnId) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      rowId,
      columnId
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleCellColorChange = (rowId, columnId, color) => {
    const rowIndex = rows.findIndex(r => r.id === rowId);
    if (rowIndex !== -1) {
      const newRows = [...rows];
      newRows[rowIndex] = {
        ...newRows[rowIndex],
          colors: {
          ...newRows[rowIndex].colors,
            [columnId]: color
          }
        };
      setRows(newRows);
      }
  };

  const getCellColor = (row, columnId) => {
    return row.colors?.[columnId] || '#ffffff';
  };

  const getCellValue = (rowId, columnId) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return 0;
    const value = row[columnId];
    if (value === undefined || value === null || value === '') return 0;
    return value;
  };

  const parseNumber = (value) => {
    if (typeof value === 'number') return value;
    if (!value || value === '' || value === '#HATA!') return 0;
    const str = value.toString().trim().replace(',', '.');
    if (str === '') return 0;
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  };

  const formatNumber = (value) => {
    if (isNaN(value) || !isFinite(value)) return '#HATA!';
    return value.toString().replace('.', ',');
  };

  const parseCellReference = (ref) => {
    try {
      const col = ref.match(/[A-Z]+/)[0];
      const row = parseInt(ref.match(/\d+/)[0]);
      return [col, row];
    } catch (error) {
      console.error('Hücre referansı ayrıştırma hatası:', error);
      return ['A', 1];
    }
  };

  const calculateFormula = (formula, rowId) => {
    try {
      if (formula.startsWith('TOPLA(')) {
        const range = formula.match(/TOPLA\((.*?)\)/)[1];
        const cells = range.split(',');
        let sum = 0;
        cells.forEach(cellRef => {
          const [col, row] = parseCellReference(cellRef);
          const value = getCellValue(row, col);
          let cellResult = value;
          if (typeof value === 'string' && value.startsWith('=')) {
            cellResult = calculateFormula(value.substring(1), row);
          }
          sum += parseNumber(cellResult);
        });
        return formatNumber(sum);
      }

      if (formula.startsWith('ORTALAMA(')) {
        const range = formula.match(/ORTALAMA\((.*?)\)/)[1];
        const cells = range.split(',');
        let sum = 0;
        let count = 0;
        cells.forEach(cellRef => {
          const [col, row] = parseCellReference(cellRef);
          const value = getCellValue(row, col);
          let cellResult = value;
          if (typeof value === 'string' && value.startsWith('=')) {
            cellResult = calculateFormula(value.substring(1), row);
          }
          const numValue = parseNumber(cellResult);
          if (!isNaN(numValue)) {
            sum += numValue;
            count++;
          }
        });
        return count > 0 ? formatNumber(sum / count) : '#HATA!';
      }

      formula = formula.replace(/([A-Z]+)(\d+)/g, (match, col, row) => {
        const value = getCellValue(parseInt(row), col);
        let cellResult = value;
        if (typeof value === 'string' && value.startsWith('=')) {
          cellResult = calculateFormula(value.substring(1), parseInt(row));
        }
        return parseNumber(cellResult).toString();
      });

      formula = formula.replace(/,/, '.');

      if (!/^[0-9+\-*/().\s]+$/.test(formula)) return '#HATA!';

      const result = Function(`"use strict";return (${formula})`)();
      if (isNaN(result) || !isFinite(result)) return '#HATA!';
      return formatNumber(result);
    } catch (error) {
      console.error('Formül hesaplama hatası:', error);
      return '#HATA!';
    }
  };

  const calculateColumnTotal = (columnId) => {
    return rows.reduce((total, row) => {
      const value = row[columnId];
      if (value === undefined || value === null || value === '') return total;
      
      if (typeof value === 'string' && value.startsWith('=')) {
        const calculatedValue = calculateFormula(value.substring(1), row.id);
        return total + (parseFloat(calculatedValue.toString().replace(/\./g, '').replace(',', '.')) || 0);
          }
      
      return total + (parseFloat(value.toString().replace(/\./g, '').replace(',', '.')) || 0);
    }, 0);
  };

  const handleFormulaDialogOpen = () => {
    if (selectedCells.length > 0) {
      const cellValues = selectedCells.map(cell => {
        const row = rows.find(r => r.id === cell.rowId);
        return row[cell.columnId];
      });
      setFormula(cellValues.join(', '));
      setFormulaDialog(true);
    }
  };

  const handleFormulaSave = () => {
    if (selectedCells.length > 0 && formula.trim() !== '') {
      const newRows = [...rows];
        selectedCells.forEach(cell => {
        const rowIndex = newRows.findIndex(r => r.id === cell.rowId);
          if (rowIndex !== -1) {
            let dynamicFormula = formula.replace(/([A-Z]+)(\d+)/g, (match, col, row) => {
              return col + cell.rowId;
            });
          newRows[rowIndex] = {
            ...newRows[rowIndex],
              [cell.columnId]: dynamicFormula
            };
          }
        });
      setRows(newRows);
    }
    setFormulaDialog(false);
  };

  const handleAddColumn = () => {
    const lastColumn = columns[columns.length - 1];
    const newId = getNextColumnId(lastColumn.id);
    
    const updatedColumns = [
      ...columns,
      {
        id: newId,
        label: newId,
        width: 100,
        backgroundColor: '#ffffff',
        isFormula: false,
        formula: ''
      }
    ];

    const updatedRows = rows.map(row => ({
      ...row,
      [newId]: ''
    }));

      setColumns(updatedColumns);
      setRows(updatedRows);
    setAddColumnDialog(false);
  };

  const getNextColumnId = (currentId) => {
    if (!currentId) return 'A';
    const lastChar = currentId.charCodeAt(currentId.length - 1);
    if (lastChar === 90) {
      if (currentId.length === 1) return 'AA';
      const prefix = currentId.slice(0, -1);
      const lastPrefixChar = prefix.charCodeAt(prefix.length - 1);
      if (lastPrefixChar === 90) {
        return prefix.slice(0, -1) + 'AA';
      }
      return prefix.slice(0, -1) + String.fromCharCode(lastPrefixChar + 1) + 'A';
    }
    return currentId.slice(0, -1) + String.fromCharCode(lastChar + 1);
  };

  useEffect(() => {
    let parsed = value;
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        parsed = { columns: [], rows: [] };
      }
    }
    if (parsed?.columns) setColumns(parsed.columns);
    if (parsed?.rows) setRows(parsed.rows);
  }, [value]);

  useEffect(() => {
    if (onChange) {
      onChange({ columns, rows });
    }
  }, [columns, rows]);

  return (
    <Box sx={{ width: '100%', overflow: 'auto' }}>
      <TableContainer component={Paper} ref={tableRef}>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50, bgcolor: '#f5f5f5' }}>
                <IconButton size="small" onClick={() => {
                  const newRow = { id: rows.length + 1 };
                  columns.forEach(col => {
                    newRow[col.id] = '';
                  });
                  setRows([...rows, newRow]);
                }}>
                  <AddIcon />
                </IconButton>
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    width: column.width,
                    bgcolor: column.backgroundColor,
                    fontWeight: 'bold',
                    textAlign: 'center',
                    position: 'relative',
                    transition: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {column.label}
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        const updatedColumns = columns.filter(col => col.id !== column.id);
                        const updatedRows = rows.map(row => {
                          const newRow = { ...row };
                          delete newRow[column.id];
                          return newRow;
                        });
                        setColumns(updatedColumns);
                        setRows(updatedRows);
                      }}
                      sx={{ transition: 'none' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell
                  sx={{
                    width: 50,
                    bgcolor: '#f5f5f5',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    transition: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {row.id}
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        const updatedRows = rows.filter(r => r.id !== row.id);
                        setRows(updatedRows);
                      }}
                      sx={{ transition: 'none' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                {columns.map((column) => {
                  const isSelected = selectedCells.some(
                    cell => cell.rowId === row.id && cell.columnId === column.id
                  );
                  const isEditing = editingCell?.rowId === row.id && editingCell?.columnId === column.id;
                  const cellColor = getCellColor(row, column.id);
                  const cellValue = row[column.id];

                  return (
                    <TableCell
                      key={column.id}
                      sx={{
                        width: column.width,
                        bgcolor: isSelected ? '#e3f2fd' : cellColor,
                        p: 0,
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'none',
                        '&:hover': {
                          bgcolor: isSelected ? '#e3f2fd' : cellColor
                        }
                      }}
                      onClick={(e) => handleCellClick(row.id, column.id, e)}
                      onDoubleClick={() => handleCellDoubleClick(row.id, column.id)}
                      onContextMenu={(e) => handleCellRightClick(e, row.id, column.id)}
                    >
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={cellValue}
                          onChange={(e) => handleCellChange(row.id, column.id, e.target.value)}
                          onBlur={() => setEditingCell(null)}
                          autoFocus
                          sx={{ 
                            '& .MuiInputBase-input': {
                              p: 1,
                              fontSize: '0.875rem'
                            },
                            '& .MuiOutlinedInput-root': {
                              transition: 'none'
                            }
                          }}
                          inputProps={{
                            style: { 
                              padding: '8px',
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            p: 1,
                            minHeight: 32,
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            transition: 'none'
                          }}
                        >
                          {typeof cellValue === 'string' && cellValue.startsWith('=') 
                            ? calculateFormula(cellValue.substring(1), row.id)
                            : cellValue}
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#f5f5f5', transition: 'none' }}>
              <TableCell 
                sx={{
                  width: 50,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  transition: 'none'
                }}
              >
                Toplam
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    width: column.width,
                    fontWeight: 'bold',
                    textAlign: 'right',
                    p: 1,
                    transition: 'none'
                  }}
                >
                  {column.type === 'number' ? calculateColumnTotal(column.id).toLocaleString('tr-TR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }) : '-'}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddColumnDialog(true)}
          sx={{ transition: 'none' }}
        >
          Sütun Ekle
        </Button>
        <Button
          variant="outlined"
          startIcon={<FunctionsIcon />}
          onClick={handleFormulaDialogOpen}
          disabled={selectedCells.length === 0}
          sx={{ transition: 'none' }}
        >
          Formül Ekle
        </Button>
      </Box>

      <Dialog 
        open={formulaDialog} 
        onClose={() => setFormulaDialog(false)}
        PaperProps={{
          sx: { transition: 'none' }
        }}
      >
        <DialogTitle>Formül Ekle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Formül"
              fullWidth
              value={formula}
              onChange={(e) => setFormula(e.target.value)}
              helperText="Örnek: =A1+B1, =TOPLA(A1:A5), =ORTALAMA(B1:B10)"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  transition: 'none'
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormulaDialog(false)} sx={{ transition: 'none' }}>İptal</Button>
          <Button onClick={handleFormulaSave} variant="contained" sx={{ transition: 'none' }}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={colorDialog} 
        onClose={() => setColorDialog(false)}
        PaperProps={{
          sx: { transition: 'none' }
        }}
      >
        <DialogTitle>Hücre Rengi Seç</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, p: 1 }}>
            {COLOR_PALETTE.map((color) => (
              <Box
                key={color.value}
                onClick={() => handleColorChange(color.value)}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: color.value,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'none',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColorDialog(false)} sx={{ transition: 'none' }}>İptal</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={addColumnDialog} 
        onClose={() => setAddColumnDialog(false)}
        PaperProps={{
          sx: { transition: 'none' }
        }}
      >
        <DialogTitle>Yeni Sütun Ekle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Arkaplan Rengi</InputLabel>
              <Select
                value={newColumn.backgroundColor}
                onChange={(e) => setNewColumn({ ...newColumn, backgroundColor: e.target.value })}
                        sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    transition: 'none'
                  }
                }}
              >
                {COLOR_PALETTE.map((color) => (
                  <MenuItem key={color.value} value={color.value} sx={{ transition: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          bgcolor: color.value,
                          border: '1px solid #ddd',
                          borderRadius: 0.5,
                          transition: 'none'
                        }}
                      />
                      <Typography>{color.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddColumnDialog(false)} sx={{ transition: 'none' }}>İptal</Button>
          <Button onClick={handleAddColumn} variant="contained" sx={{ transition: 'none' }}>
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: { transition: 'none' }
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Renk Seç</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
            {COLOR_PALETTE.map((color) => (
              <Box
                key={color.value}
                onClick={() => {
                  handleCellColorChange(contextMenu.rowId, contextMenu.columnId, color.value);
                  handleContextMenuClose();
                }}
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: color.value,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'none',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

CalculationField.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
};

export default CalculationField; 